import React from 'react';
import PropTypes from 'prop-types';

import { Icon, Button } from 'modules/Core/Common';
import { UserAvatar } from 'modules/Users/Common';
import { ItemToggle } from 'modules/Core/Common/HeaderInfo/components';
import { capitalizeWord } from 'modules/Utils';
import { getExerciseStatus } from 'modules/Submissions/Utils';
import { format } from 'modules/Utils/Date';
import { useAuth } from 'modules/Auth/Hooks';
import i18n from 'i18next';

import * as S from './StudentCard.styles';

export const StudentCard = ({
  exercise,
  isNext,
  onButtonClick,
  onChangeReviewAvailable,
  onChangeScoreAvailable,
  onChangeCertificateAvailable,
  isChangingReviewAvailable,
  isChangingScoreAvailable,
  isChangingCertificateAvailable,
}) => {
  const { userTimeFormatSeconds, userDateFormat } = useAuth();
  const I18N_PREFIX = 'commons.header-info';

  if (!exercise) {
    return (
      <S.StudentCard>
        <S.Empty>
          <div>
            <Icon name="student" />
          </div>
          {i18n.t('modules.submissions.common.modal.card.more-students')}
        </S.Empty>
      </S.StudentCard>
    );
  }

  if (!exercise) {
    return (
      <S.StudentCard>
        <S.Empty>
          <div>
            <Icon name="student" />
          </div>
          {i18n.t('modules.submissions.common.modal.card.more-students')}
        </S.Empty>
      </S.StudentCard>
    );
  }

  if (!exercise) {
    return (
      <S.StudentCard>
        <S.Empty>
          <div>
            <Icon name="student" />
          </div>
          {i18n.t('modules.submissions.common.modal.card.more-students')}
        </S.Empty>
      </S.StudentCard>
    );
  }

  return (
    <S.StudentCard>
      <S.StudentAvatar>
        <UserAvatar
          name={exercise?.student?.name}
          email={exercise?.student?.email}
          avatar={exercise?.student?.avatar?.url}
        >
          <UserAvatar.Photo />
          <UserAvatar.Name />
          <UserAvatar.Card />
        </UserAvatar>
      </S.StudentAvatar>
      <S.StudentCardBody>
        <S.FirstColumn>
          <S.Date>
            <h6>{i18n.t('modules.submissions.common.modal.card.date')}</h6>
            {exercise?.created_at ? (
              <p>
                {format(new Date(exercise?.created_at), userDateFormat)}&nbsp;
                <span>
                  {format(
                    new Date(exercise?.created_at),
                    userTimeFormatSeconds
                  )}
                </span>
              </p>
            ) : (
              <p>-</p>
            )}
          </S.Date>
          <S.Category>
            <h6>{i18n.t('modules.submissions.common.modal.card.category')}</h6>
            <p>{capitalizeWord(exercise?.category_tag || '-')}</p>
          </S.Category>
          <S.Evaluated>
            <h6>{i18n.t('modules.submissions.common.modal.card.evaluated')}</h6>
            <S.EvaluatedLabel
              status={getExerciseStatus(exercise?.status_exercise)}
            >
              {getExerciseStatus(exercise?.status_exercise)}
              <Icon
                fontSize="0.8rem"
                name={exercise?.status_exercise === 0 ? 'pending' : 'check'}
                right
              />
            </S.EvaluatedLabel>
          </S.Evaluated>
        </S.FirstColumn>
        <S.SecondColumn>
          {onChangeReviewAvailable && (
            <ItemToggle
              title={i18n.t(`${I18N_PREFIX}.review-available`)}
              active={!!exercise?.review_available}
              loading={isChangingReviewAvailable}
              onChange={({ target }) => onChangeReviewAvailable(target.checked)}
            />
          )}
          {onChangeScoreAvailable && (
            <ItemToggle
              title={i18n.t(`${I18N_PREFIX}.score-available`)}
              active={!!exercise?.score_available}
              loading={isChangingScoreAvailable}
              onChange={({ target }) => onChangeScoreAvailable(target.checked)}
            />
          )}
          {onChangeCertificateAvailable && (
            <ItemToggle
              title={i18n.t(`${I18N_PREFIX}.certificate-available`)}
              active={!!exercise?.certificate_available}
              loading={isChangingCertificateAvailable}
              onChange={({ target }) =>
                onChangeCertificateAvailable(target.checked)
              }
            />
          )}
        </S.SecondColumn>
      </S.StudentCardBody>
      <S.Separator />
      <S.ButtonContainer>
        <Button
          type="button"
          small
          transparent
          disabled={!exercise}
          onClick={onButtonClick}
        >
          {isNext
            ? i18n.t('modules.submissions.common.modal.card.next')
            : i18n.t('modules.submissions.common.modal.card.previous')}
        </Button>
      </S.ButtonContainer>
    </S.StudentCard>
  );
};

StudentCard.propTypes = {
  exercise: PropTypes.object,
  isNext: PropTypes.bool,
  onButtonClick: PropTypes.func,
  onChangeReviewAvailable: PropTypes.func,
  onChangeScoreAvailable: PropTypes.func,
  onChangeCertificateAvailable: PropTypes.func,
  isChangingReviewAvailable: PropTypes.bool,
  isChangingScoreAvailable: PropTypes.bool,
  isChangingCertificateAvailable: PropTypes.bool,
};
